import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { Controller } from 'react-hook-form';
import { ComboSelect, TextboxInput, Typography } from '../../components/atoms';
import { getAdminUserRoles, FEATURE_NAMES } from '../../common/constants';
import { useAppSelector } from '../../common/hooks';
import { useTranslation } from 'react-i18next';
import { checkFFEnabled } from '../../utils';

const GridContainer: any = styled(Grid)(() => ({
    '.MuiInputBase-input': {
        boxSizing: 'border-box'
    },
    marginTop: '2px'
}));

const ErrorSpan = styled('span')(({ theme }) => ({
    color: theme.palette.berry[400],
    fontSize: '12px'
}));

export default function UserInfo({
    onSubmit,
    handleSubmit,
    control,
    errors,
    translation,
    emailExistsError,
    userRole
}: any) {
    const { t } = useTranslation();
    const admin_userRoles = getAdminUserRoles(t);
    const formattedOptions: Array<{ value: string; label: string }> = useAppSelector(
        (state) => state?.auth?.user?.admin_tenantList
    );
    const userRoleOptions = admin_userRoles.filter(
        (item) => item.accessibleTo.includes(userRole) && item.value !== 'ftadmin'
    );
    const [selectedOption] = useState('');
    const [selectedLanguage] = useState('');
    const [selectedUserRole] = useState('');
    let formattedLanguageOptions: Array<{ value: string; label: string }> = [
        { value: 'en_US', label: 'English (US)' },
        { value: 'es_MX', label: 'Español (México)' }
    ];

    return (
        <form id="add-user-form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <GridContainer container spacing={2}>
                <GridContainer item xs={12} sm={6} md={3} lg={3} sx={{ lineHeight: '20px' }}>
                    <Typography variant="body1">{translation.firstName.label}</Typography>
                    <Controller
                        name="firstName"
                        control={control}
                        rules={{
                            required: {
                                value: true,
                                message: translation.firstName.requiredErrorMessage
                            },
                            pattern: {
                                value: /^(?!\s)(?!.*\s$)([A-Za-z0-9\s’'.\-&/+()áéíóúüñÁÉÍÓÚÜÑàâäèêëîïôöùûçÀÂÄÈÊËÎÏÔÖÙÛÇ])*$/i,
                                message: translation.firstName.validationErrorMessage
                            },
                            maxLength: {
                                value: 50,
                                message: translation.firstName.maxValidationErrorMessage
                            },
                            minLength: {
                                value: 2,
                                message: translation.firstName.minValidationErrorMessage
                            }
                        }}
                        render={({ field: { onChange, onBlur, value, ref } }) => (
                            <TextboxInput
                                onBlur={onBlur}
                                onChange={onChange}
                                value={value}
                                inputRef={ref}
                                error={!!errors.firstName}
                                type="text"
                                placeholder={translation.firstName.placeholder}
                            />
                        )}
                    />
                    {errors.firstName && (
                        <ErrorSpan role="alert">{t(errors.firstName.message[0], { user: t('TEXT_USER') })}</ErrorSpan>
                    )}
                </GridContainer>
                <GridContainer item xs={12} sm={6} md={3} lg={3} sx={{ lineHeight: '20px' }}>
                    <Typography variant="body1">{translation.lastName.label}</Typography>
                    <Controller
                        name="lastName"
                        control={control}
                        rules={{
                            required: {
                                value: true,
                                message: translation.lastName.requiredErrorMessage
                            },
                            pattern: {
                                value: /^(?!\s)(?!.*\s$)([A-Za-z0-9\s’'.\-&/+()áéíóúüñÁÉÍÓÚÜÑàâäèêëîïôöùûçÀÂÄÈÊËÎÏÔÖÙÛÇ])*$/i,
                                message: translation.lastName.validationErrorMessage
                            },
                            maxLength: {
                                value: 50,
                                message: translation.lastName.maxValidationErrorMessage
                            },
                            minLength: {
                                value: 2,
                                message: translation.lastName.minValidationErrorMessage
                            }
                        }}
                        render={({ field: { onChange, onBlur, value, ref } }) => (
                            <TextboxInput
                                onBlur={onBlur}
                                onChange={onChange}
                                value={value}
                                inputRef={ref}
                                error={!!errors.lastName}
                                type="text"
                                placeholder={translation.lastName.placeholder}
                            />
                        )}
                    />
                    {errors.lastName && (
                        <ErrorSpan role="alert">{t(errors.lastName.message[0], { user: t('TEXT_USER') })}</ErrorSpan>
                    )}
                </GridContainer>
                {userRole === 'ftadmin' ? (
                    <GridContainer item xs={12} sm={6} md={3} lg={3} sx={{ lineHeight: '20px' }}>
                        <Typography variant="body1">{translation.tenantName.label}</Typography>
                        <Controller
                            name="tenantName"
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: translation.tenantName.validationErrorMessage
                                }
                            }}
                            render={({ field }) => (
                                <ComboSelect
                                    {...field}
                                    onSelectChange={field.onChange}
                                    selectedValue={selectedOption || ''}
                                    options={formattedOptions}
                                />
                            )}
                        />
                        {errors.tenantName && <ErrorSpan role="alert">{t(...errors.tenantName.message)}</ErrorSpan>}
                    </GridContainer>
                ) : null}
                <GridContainer item xs={12} sm={6} md={3} lg={3} sx={{ lineHeight: '20px' }}>
                    <Typography variant="body1">{translation.email.label}</Typography>
                    <Controller
                        name="emailAddress"
                        control={control}
                        aria-invalid={errors?.emailAddress || emailExistsError ? 'true' : 'false'}
                        rules={{
                            required: {
                                value: true,
                                message: translation.email.requiredErrorMessage
                            },
                            pattern: {
                                value: checkFFEnabled(FEATURE_NAMES['GMAIL_IDS_RESTRICTION_FE_AEX-2879'])
                                    ? /^[A-Z0-9._%+-]+@(?!gmail\.com$)[A-Z0-9.-]+\.[A-Z]{2,4}$/i
                                    : /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                                message: translation.email.validationErrorMessage
                            }
                        }}
                        render={({ field: { onChange, onBlur, value, ref } }) => (
                            <TextboxInput
                                onBlur={onBlur}
                                onChange={onChange}
                                value={value}
                                inputRef={ref}
                                error={!!errors?.emailAddress || !!emailExistsError}
                                placeholder={translation.email.placeholder}
                            />
                        )}
                    />
                    {(errors.emailAddress || emailExistsError) && (
                        <ErrorSpan role="alert">
                            {errors?.emailAddress
                                ? t(errors.emailAddress.message[0], { user: t('TEXT_USER') })
                                : emailExistsError}
                        </ErrorSpan>
                    )}
                </GridContainer>
                <GridContainer item xs={12} sm={6} md={3} lg={3} sx={{ lineHeight: '20px' }}>
                    <Typography variant="body1">{translation.user_role.label}</Typography>
                    <Controller
                        name="user_role"
                        control={control}
                        rules={{
                            required: {
                                value: true,
                                message: translation.user_role.validationErrorMessage
                            }
                        }}
                        render={({ field }) => (
                            <ComboSelect
                                {...field}
                                onSelectChange={field.onChange}
                                selectedValue={selectedUserRole || ''}
                                options={userRoleOptions}
                            />
                        )}
                    />
                    {errors.user_role && <ErrorSpan role="alert">{t(...errors.user_role.message)}</ErrorSpan>}
                </GridContainer>
                <GridContainer item xs={12} sm={6} md={3} lg={3} sx={{ lineHeight: '20px' }}>
                    <Typography variant="body1" sx={{ whiteSpace: 'nowrap' }}>
                        {translation.language.label}
                    </Typography>
                    <Controller
                        name="language"
                        control={control}
                        rules={{
                            required: {
                                value: true,
                                message: translation.language.validationErrorMessage
                            }
                        }}
                        render={({ field }) => (
                            <ComboSelect
                                {...field}
                                onSelectChange={field.onChange}
                                selectedValue={selectedLanguage || ''}
                                options={formattedLanguageOptions}
                            />
                        )}
                    />
                    {errors.language && <ErrorSpan role="alert">{t(...errors.language.message)}</ErrorSpan>}
                </GridContainer>
            </GridContainer>
        </form>
    );
}
